import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/project/jain_elibrary-admin/components/ui/select";
import { Loader2 } from "lucide-react";

export type AdminPaginationBarProps = {
  page: number;
  pageSize: number;
  totalCount: number;
  loading?: boolean;
  onPageChange: (nextPage: number) => void;
  onPageSizeChange?: (nextPageSize: number) => void;
  /** Shown when `onPageSizeChange` is set. */
  pageSizeOptions?: number[];
};

export function AdminPaginationBar({
  page,
  pageSize,
  totalCount,
  loading,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
}: AdminPaginationBarProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showControls = totalCount > 0;

  const from = showControls ? (page - 1) * pageSize + 1 : 0;
  const to = showControls ? Math.min(page * pageSize, totalCount) : 0;

  return (
    <div className="flex flex-col items-stretch justify-between gap-4 rounded-lg border border-border bg-muted/10 px-3 py-3 sm:flex-row sm:items-center sm:px-4">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {loading ? (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Loading…
          </span>
        ) : null}
        {showControls ? (
          <span>
            Showing{" "}
            <span className="font-semibold text-foreground">{from}</span>
            {" — "}
            <span className="font-semibold text-foreground">{to}</span>
            {" of "}
            <span className="font-semibold text-foreground">{totalCount}</span>
          </span>
        ) : (
          <span>No records on this page.</span>
        )}
        {onPageSizeChange ? (
          <label className="ml-auto flex items-center gap-2 sm:ml-0">
            <span className="sr-only">Rows per page</span>
            <span className="hidden sm:inline">Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={v => onPageSizeChange(Number(v))}
              disabled={loading}
            >
              <SelectTrigger className="h-8 w-[4.5rem] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map(n => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-1.5 sm:justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs"
          onClick={() => onPageChange(1)}
          disabled={!showControls || page <= 1 || loading}
        >
          First
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs"
          onClick={() => onPageChange(page - 1)}
          disabled={!showControls || page <= 1 || loading}
        >
          Previous
        </Button>
        <div
          className="min-w-[5.5rem] rounded-md border border-border bg-background px-3 py-1.5 text-center text-xs font-semibold tabular-nums"
          aria-live="polite"
        >
          {showControls ? `${page} / ${totalPages}` : "—"}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs"
          onClick={() => onPageChange(page + 1)}
          disabled={!showControls || page >= totalPages || loading}
        >
          Next
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs"
          onClick={() => onPageChange(totalPages)}
          disabled={!showControls || page >= totalPages || loading}
        >
          Last
        </Button>
      </div>
    </div>
  );
}
